import React, { useState, useEffect, useCallback } from 'react';
import issueService from '../services/issueService';
import maintenanceService from '../services/maintenanceService';
import complianceService from '../services/complianceService';
import assetService from '../services/assetService';
import LabTechnicianSidebar from './Techniciansidebar';
import { FaCalendarAlt, FaChartBar, FaChartPie, FaInfoCircle } from 'react-icons/fa';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';
import '../styles/LabTechnicianDashboard_new.css';
import '../styles/ComplianceDashboard.css';


const LabTechnicianDashboard = ({ user }) => {
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
    unassigned: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [proofFiles, setProofFiles] = useState([]);
  
  const [activeTab] = useState('issues');
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [maintStatusFilter] = useState('');

  const [complianceDaysFilter] = useState('30');
  
  // Compliance edit modal states
  const [editingComplianceAsset, setEditingComplianceAsset] = useState(null);
  const [complianceEditForm, setComplianceEditForm] = useState({
    assetName: '',
    complianceDate: ''
  });
  const [complianceEditLoading, setComplianceEditLoading] = useState(false);

  // Fetch issues and stats
  const fetchIssuesAndStats = useCallback(async () => {
    try {
      setLoading(true);
     

      // Fetch all issues
      const issuesData = await issueService.getAllIssues();
      setIssues(issuesData.data || []);

      // Fetch stats
      const statsData = await issueService.getTechnicianStats();
      if (statsData.success) {
        setStats(statsData.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch maintenance requests assigned to this technician
  const fetchMaintenanceRequests = useCallback(async () => {
    try {
   
      const response = await maintenanceService.getMyMaintenanceRequests(maintStatusFilter || null);
      setMaintenanceRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching maintenance requests:', error);
    }
  }, [maintStatusFilter]);





 

  // Calculate chart data
  const getIssueStatusData = () => {
    return [
      {
        name: 'Pending',
        value: issues.filter(i => i.status === 'pending').length,
        color: '#f59e0b'
      },
      {
        name: 'Accepted',
        value: issues.filter(i => i.status === 'accepted').length,
        color: '#8b5cf6'
      },
      {
        name: 'In Progress',
        value: issues.filter(i => i.status === 'in_progress').length,
        color: '#3b82f6'
      },
      {
        name: 'Resolved',
        value: issues.filter(i => i.status === 'resolved').length,
        color: '#10b981'
      },
      {
        name: 'Rejected',
        value: issues.filter(i => i.status === 'rejected').length,
        color: '#ef4444'
      }
    ].filter(item => item.value > 0);
  };

  const getIssueSeverityData = () => {
    return [
      {
        name: 'Critical',
        value: issues.filter(i => i.severity === 'critical').length,
        color: '#991b1b'
      },
      {
        name: 'High',
        value: issues.filter(i => i.severity === 'high').length,
        color: '#b45309'
      },
      {
        name: 'Medium',
        value: issues.filter(i => i.severity === 'medium').length,
        color: '#d97706'
      },
      {
        name: 'Low',
        value: issues.filter(i => i.severity === 'low').length,
        color: '#10b981'
      }
    ].filter(item => item.value > 0);
  };

  const getMaintenanceStatusData = () => {
    return [
      {
        name: 'Pending',
        value: maintenanceRequests.filter(m => m.status === 'pending').length,
        color: '#f59e0b'
      },
      {
        name: 'In Progress',
        value: maintenanceRequests.filter(m => m.status === 'in_progress').length,
        color: '#3b82f6'
      },
      {
        name: 'Completed',
        value: maintenanceRequests.filter(m => m.status === 'completed').length,
        color: '#10b981'
      },
      {
        name: 'Rejected',
        value: maintenanceRequests.filter(m => m.status === 'rejected').length,
        color: '#ef4444'
      }
    ].filter(item => item.value > 0);
  };

  const getWorkloadByWeek = () => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
      
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      const issuesCount = issues.filter(issue => new Date(issue.reportedAt) >= dayStart && new Date(issue.reportedAt) <= dayEnd).length;
      const maintCount = maintenanceRequests.filter(m => new Date(m.reportedDate) >= dayStart && new Date(m.reportedDate) <= dayEnd).length;

      last7Days.push({
        date: dateStr,
        Issues: issuesCount,
        Maintenance: maintCount
      });
    }
    return last7Days;
  };

  const handleChecklistItemToggle = async (itemIndex) => {
    if (!selectedIssue) return;

    try {
      const updatedChecklist = [...selectedIssue.checklist];
      updatedChecklist[itemIndex] = {
        ...updatedChecklist[itemIndex],
        completed: !updatedChecklist[itemIndex].completed
      };

     

      const response = await issueService.updateIssueStatus(selectedIssue._id, {
        checklist: updatedChecklist
      });

   

      if (response.success) {
        setSelectedIssue(response.data);
        // Update issues list
        setIssues(issues.map(issue =>
          issue._id === selectedIssue._id ? response.data : issue
        ));
      }
    } catch (error) {
      console.error('Error updating checklist:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert('Error updating checklist: ' + (error.response?.data?.message || error.message));
    }
  };



  const getChecklistProgress = () => {
    if (!selectedIssue || !selectedIssue.checklist || selectedIssue.checklist.length === 0) {
      return 0;
    }
    const completed = selectedIssue.checklist.filter(item => item.completed).length;
    return Math.round((completed / selectedIssue.checklist.length) * 100);
  };

  const handleFileUpload = async (file) => {
    if (!file || !selectedIssue) return;

    try {
      setLoading(true);
      const response = await issueService.uploadAttachment(selectedIssue._id, file);
      if (response.success) {
        setSelectedIssue(response.data);
        // Update issues list
        setIssues(issues.map(issue =>
          issue._id === selectedIssue._id ? response.data : issue
        ));
        alert('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      setLoading(true);
      const response = await issueService.deleteAttachment(selectedIssue._id, attachmentId);
      if (response.success) {
        setSelectedIssue(response.data);
        // Update issues list
        setIssues(issues.map(issue =>
          issue._id === selectedIssue._id ? response.data : issue
        ));
        alert('File deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file');
    } finally {
      setLoading(false);
    }
  };

  const handleProofFileUpload = (event) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setProofFiles([...proofFiles, ...newFiles]);
    }
  };

  const handleRemoveProofFile = (index) => {
    setProofFiles(proofFiles.filter((_, i) => i !== index));
  };

  // Fetch compliance due assets
  const fetchComplianceDueAssets = useCallback(async () => {
    try {
      await complianceService.getComplianceDueAssets(parseInt(complianceDaysFilter));
      await complianceService.getComplianceStats();
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    }
  }, [complianceDaysFilter]);

  useEffect(() => {
    fetchIssuesAndStats();
  }, [fetchIssuesAndStats]);

  useEffect(() => {
    if (activeTab === 'maintenance') {
      fetchMaintenanceRequests();
    }
  }, [activeTab, maintStatusFilter, fetchMaintenanceRequests]);

  useEffect(() => {
    if (activeTab === 'compliance') {
      fetchComplianceDueAssets();
    }
  }, [activeTab, complianceDaysFilter, fetchComplianceDueAssets]);

  const handleStatusChange = async (issueId, newStatus) => {
    try {
      setLoading(true);
      const updateData = { status: newStatus };

      if (newStatus === 'resolved' && remarks) {
        updateData.technicianRemarks = remarks;
      }

      if (newStatus === 'rejected' && rejectionReason) {
        updateData.rejectionReason = rejectionReason;
      }

      // Handle proof file uploads for resolved status
      if (newStatus === 'resolved' && proofFiles.length > 0) {
        const formData = new FormData();
        formData.append('status', 'resolved');
        formData.append('technicianRemarks', remarks);
        formData.append('context', 'closure');
        
        // Add proof files
        proofFiles.forEach((file, index) => {
          formData.append(`proofFiles`, file);
        });

        // Upload files and update status
        const response = await issueService.updateIssueStatusWithProof(issueId, formData);
        if (response.success) {
          setIssues(issues.map(issue =>
            issue._id === issueId ? response.data : issue
          ));
          setSelectedIssue(null);
          setStatusAction(null);
          setRemarks('');
          setProofFiles([]);
          fetchIssuesAndStats();
        }
      } else {
        const response = await issueService.updateIssueStatus(issueId, updateData);
        if (response.success) {
          // Update local state
          setIssues(issues.map(issue =>
            issue._id === issueId ? response.data : issue
          ));
          setSelectedIssue(null);
          setStatusAction(null);
          setRemarks('');
          setProofFiles([]);
          setRejectionReason('');
          // Refresh stats
          fetchIssuesAndStats();
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating issue status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    return `status-badge status-${status.replace('_', '-')}`;
  };

  const getSeverityBadgeClass = (severity) => {
    return `severity-badge severity-${severity}`;
  };



  const handleSaveComplianceEdit = async () => {
    if (!complianceEditForm.complianceDate) {
      alert('Please select a compliance date');
      return;
    }

    setComplianceEditLoading(true);
    try {
      await assetService.updateAsset(editingComplianceAsset, {
        complianceDate: new Date(complianceEditForm.complianceDate).toISOString()
      });
      
      // Reset form
      setEditingComplianceAsset(null);
      setComplianceEditForm({ assetName: '', complianceDate: '' });
      alert('Compliance date updated successfully');
    } catch (error) {
      console.error('Error updating compliance date:', error);
      alert('Failed to update compliance date');
    } finally {
      setComplianceEditLoading(false);
    }
  };

  const handleCancelComplianceEdit = () => {
    setEditingComplianceAsset(null);
    setComplianceEditForm({ assetName: '', complianceDate: '' });
  };

  return (
    <div className="lab-technician-page">
      <LabTechnicianSidebar user={user} />
      <div className="page-container">
        <div className="lab-technician-dashboard">
      <div className="tech-dashboard-header">
        <h2>Asset Technician Dashboard</h2>
    
      </div>
      {/* Stats Cards */}
      <div className="stats-container-technician">
        <div className="stat-card stat-pending">
          <h3>Pending</h3>
          <p className="stat-number">{stats.pending}</p>
        </div>
        <div className="stat-card stat-in-progress">
          <h3>In Progress</h3>
          <p className="stat-number">{stats.inProgress}</p>
        </div>
        <div className="stat-card stat-resolved">
          <h3>Resolved</h3>
          <p className="stat-number">{stats.resolved}</p>
        </div>
        <div className="stat-card stat-rejected">
          <h3>Rejected</h3>
          <p className="stat-number">{stats.rejected}</p>
        </div>
        <div className="stat-card stat-unassigned">
          <h3>Unassigned</h3>
          <p className="stat-number">{stats.unassigned}</p>
        </div>
      </div>

      {/* Analytics & Charts Section */}
      <div className="analytics-section">
        <h3 className="analytics-title">
          <FaChartBar style={{ marginRight: '10px' }} /> 
          Performance Analytics
        </h3>

        <div className="charts-grid">
          {/* Issues Status Distribution */}
          <div className="chart-card">
            <h4>
              <FaChartPie style={{ marginRight: '8px' }} />
              Issue Status Distribution
            </h4>
            {getIssueStatusData().length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getIssueStatusData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getIssueStatusData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No data available</div>
            )}
          </div>

          {/* Issue Severity Distribution */}
          <div className="chart-card">
            <h4>
              <FaChartBar style={{ marginRight: '8px' }} />
              Issue Severity Distribution
            </h4>
            {getIssueSeverityData().length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getIssueSeverityData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" radius={[8, 8, 0, 0]}>
                    {getIssueSeverityData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No data available</div>
            )}
          </div>

          {/* Maintenance Status Distribution */}
          <div className="chart-card">
            <h4>
              <FaChartPie style={{ marginRight: '8px' }} />
              Maintenance Status Distribution
            </h4>
            {getMaintenanceStatusData().length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getMaintenanceStatusData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getMaintenanceStatusData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No data available</div>
            )}
          </div>

          {/* 7-Day Workload Trend */}
          <div className="chart-card">
            <h4>
              <FaChartBar style={{ marginRight: '8px' }} />
              7-Day Workload Trend
            </h4>
            {getWorkloadByWeek().length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={getWorkloadByWeek()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Issues" stroke="#f59e0b" dot={{ fill: '#f59e0b' }} connectNulls />
                  <Line type="monotone" dataKey="Maintenance" stroke="#3b82f6" dot={{ fill: '#3b82f6' }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
   

  

      {/* === COMPLIANCE DUE TAB === */}
  

      {/* Compliance Date Edit Modal */}
      {editingComplianceAsset && (
        <div className="modal-overlay" onClick={handleCancelComplianceEdit}>
          <div className="modal-content compliance-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <span className="modal-icon">
                  <FaCalendarAlt style={{ color: '#dc2626' }} />
                </span>
                <div>
                  <h3>Edit Compliance Date</h3>
                  <span className="modal-subtitle">{complianceEditForm.assetName}</span>
                </div>
              </div>
              <button
                className="close-btn"
                onClick={handleCancelComplianceEdit}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="compliance-edit-form">
                <div className="form-group">
                  <label htmlFor="compliance-date-input">Compliance Date:</label>
                  <input
                    id="compliance-date-input"
                    type="date"
                    value={complianceEditForm.complianceDate}
                    onChange={(e) => setComplianceEditForm(prev => ({
                      ...prev,
                      complianceDate: e.target.value
                    }))}
                    className="date-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="compliance-info">
                  <p>
                    <FaInfoCircle style={{ marginRight: '8px', color: '#3b82f6' }} />
                    Set a compliance due date for this asset
                  </p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCancelComplianceEdit}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveComplianceEdit}
                disabled={complianceEditLoading}
              >
                {complianceEditLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue Details Modal */}

      {selectedIssue && (
        <div className="modal-overlay" onClick={() => setSelectedIssue(null)}>
          <div className="modal-content issue-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <span className="modal-icon">🔧</span>
                <div>
                  <h3>Issue Details</h3>
                  <span className="modal-subtitle">#{selectedIssue._id?.slice(-6).toUpperCase()}</span>
                </div>
              </div>
              <button
                className="close-btn"
                onClick={() => setSelectedIssue(null)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Status & Severity Badges Row */}
              <div className="modal-badges-row">
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Status</span>
                  <span className={`modal-badge ${getStatusBadgeClass(selectedIssue.status)}`}>
                    {selectedIssue.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Severity</span>
                  <span className={`modal-badge ${getSeverityBadgeClass(selectedIssue.severity)}`}>
                    {selectedIssue.severity.toUpperCase()}
                  </span>
                </div>
                <div className="modal-badge-group">
                  <span className="modal-badge-label">Type</span>
                  <span className="modal-badge modal-badge-type">
                    {selectedIssue.issueType.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Asset Info Card */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Asset Information</h4>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="info-label">Asset Name</span>
                    <span className="info-value">{selectedIssue.assetId?.assetName || '—'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Serial Number</span>
                    <span className="info-value">{selectedIssue.assetId?.serialNumber || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Report Info Card */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Report Information</h4>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="info-label">Reported By</span>
                    <span className="info-value">{selectedIssue.reportedBy?.name || 'Asset Incharge'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Reported Date</span>
                    <span className="info-value">{new Date(selectedIssue.reportedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Description</h4>
                <p className="modal-description">{selectedIssue.description}</p>
              </div>

              {selectedIssue.checklist && selectedIssue.checklist.length > 0 && (
                <div className="modal-info-card">
                  <h4 className="modal-card-title">Task Checklist <span className="checklist-pct">{getChecklistProgress()}%</span></h4>
                  <div className="checklist-progress">
                    <div className="progress-bar" style={{ width: `${getChecklistProgress()}%` }}></div>
                  </div>
                  <div className="checklist-interactive">
                    {selectedIssue.checklist.map((item, idx) => (
                      <div key={idx} className="checklist-item">
                        <input
                          type="checkbox"
                          id={`checklist-${idx}`}
                          checked={item.completed || false}
                          onChange={() => handleChecklistItemToggle(idx)}
                          disabled={selectedIssue.status === 'resolved' || selectedIssue.status === 'closed'}
                        />
                        <label htmlFor={`checklist-${idx}`} className={item.completed ? 'completed' : ''}>
                          {typeof item === 'string' ? item : item.text}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedIssue.technicianRemarks && (
                <div className="modal-info-card modal-remarks-card">
                  <h4 className="modal-card-title">Technician Remarks</h4>
                  <p className="modal-description">{selectedIssue.technicianRemarks}</p>
                </div>
              )}

              {selectedIssue.rejectionReason && (
                <div className="modal-info-card modal-rejection-card">
                  <h4 className="modal-card-title">Rejection Reason</h4>
                  <p className="modal-description">{selectedIssue.rejectionReason}</p>
                </div>
              )}

              {/* File Attachments Section */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Attachments</h4>
                <div className="attachments-section">
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file-input"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      disabled={loading}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="btn btn-upload"
                      onClick={() => document.getElementById('file-input').click()}
                      disabled={loading}
                    >
                      + Upload File
                    </button>
                  </div>

                  {selectedIssue.attachments && selectedIssue.attachments.length > 0 && (
                    <div className="attachments-list">
                      <h4>Files ({selectedIssue.attachments.length}):</h4>
                      <ul>
                        {selectedIssue.attachments.map((attachment) => (
                          <li key={attachment._id} className="attachment-item">
                            <div className="attachment-info">
                              <span className="filename">{attachment.originalName}</span>
                              <span className="filesize">({(attachment.size / 1024).toFixed(2)} KB)</span>
                              <span className="upload-date">
                                Uploaded: {new Date(attachment.uploadedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <button
                              className="btn btn-delete-file"
                              onClick={() => handleDeleteAttachment(attachment._id)}
                              disabled={loading}
                              title="Delete file"
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="modal-footer">
              {selectedIssue.status === 'pending' && (
                <>
                  <button
                    className="btn btn-accept"
                    onClick={() => handleStatusChange(selectedIssue._id, 'accepted')}
                    disabled={loading}
                  >
                    Accept
                  </button>
                  <button
                    className="btn btn-reject"
                    onClick={() => setStatusAction('reject')}
                    disabled={loading}
                  >
                    Reject
                  </button>
                </>
              )}

              {selectedIssue.status === 'accepted' && (
                <button
                  className="btn btn-progress"
                  onClick={() => handleStatusChange(selectedIssue._id, 'in_progress')}
                  disabled={loading}
                >
                  Mark as In Progress
                </button>
              )}

              {selectedIssue.status === 'in_progress' && (
                <button
                  className="btn btn-resolve"
                  onClick={() => setStatusAction('resolve')}
                  disabled={loading}
                >
                  Mark as Resolved
                </button>
              )}

              <button
                className="btn btn-close"
                onClick={() => setSelectedIssue(null)}
              >
                Close
              </button>
            </div>

            {/* Rejection Form */}
            {statusAction === 'reject' && (
              <div className="action-form">
                <h4>Reject Issue</h4>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection reason"
                  rows="3"
                  required
                />
                <div className="form-buttons">
                  <button
                    className="btn btn-submit"
                    onClick={() =>
                      handleStatusChange(selectedIssue._id, 'rejected')
                    }
                    disabled={!rejectionReason || loading}
                  >
                    Submit Rejection
                  </button>
                  <button
                    className="btn btn-cancel"
                    onClick={() => setStatusAction(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Resolution Form */}
            {statusAction === 'resolve' && (
              <div className="action-form">
                <h4>Mark as Resolved</h4>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter technician remarks"
                  rows="3"
                  required
                />
                
                {/* Proof File Upload Section */}
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Upload Proof (Images/Videos) - Optional
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleProofFileUpload}
                    style={{
                      display: 'block',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginBottom: '10px'
                    }}
                  />
                  <small style={{ color: '#666' }}>
                    Accepted formats: Images (JPG, PNG, GIF) and Videos (MP4, WebM, MOV)
                  </small>
                </div>

                {/* Proof Files List */}
                {proofFiles.length > 0 && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#f0f4f8',
                    borderRadius: '4px'
                  }}>
                    <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                      Selected Proof Files: ({proofFiles.length})
                    </label>
                    {proofFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: '#fff',
                          marginBottom: '6px',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <span style={{ fontSize: '13px' }}>
                          {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveProofFile(index)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ff6b6b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-buttons">
                  <button
                    className="btn btn-submit"
                    onClick={() =>
                      handleStatusChange(selectedIssue._id, 'resolved')
                    }
                    disabled={!remarks || loading}
                  >
                    Submit Resolution
                  </button>
                  <button
                    className="btn btn-cancel"
                    onClick={() => {
                      setStatusAction(null);
                      setProofFiles([]);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default LabTechnicianDashboard;
